// TanStack Query keys, options and cache helpers for the attention feed. Keys are narrow so SSE
// events invalidate exactly one board, card or work list (never a full-state refetch).
import { keepPreviousData, queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import type { BoardView, CardSummary, CardView, FeedSummary } from "@shared/api";
import { feedApi, type AuditParams } from "./feedApi";

export const feedKeys = {
  feeds: ["feeds"] as const,
  board: (feedId: string) => ["board", feedId] as const,
  /** Not under "board": board helpers read every ["board", …] entry as a BoardView. */
  snoozed: (feedId: string) => ["snoozed", feedId] as const,
  card: (cardId: string) => ["card", cardId] as const,
  /** Prefixed by thread key so a `thread` SSE event can refresh the drawer too. */
  cardThread: (threadKey: string, cardId: string, images: boolean) =>
    ["cardThread", threadKey, cardId, { images }] as const,
  work: (feedId: string, statuses: string) => ["work", feedId, statuses] as const,
  config: (feedId: string) => ["feedConfig", feedId] as const,
  docs: ["docs"] as const,
  doc: (key: string) => ["doc", key] as const,
  proposals: (status: string) => ["proposals", status] as const,
  audit: (params: AuditParams) => ["audit", params] as const,
  hidden: ["hidden"] as const,
};

export const feedsQuery = queryOptions({
  queryKey: feedKeys.feeds,
  queryFn: ({ signal }) => feedApi.feeds(signal),
  staleTime: 60_000,
});

/** Phase 1 has one feed ("inbox"); the board shows the first active one. */
export function pickActiveFeed(feeds: readonly FeedSummary[] | undefined): FeedSummary | null {
  if (!feeds?.length) return null;
  return feeds.find((f) => f.state === "active") ?? feeds[0] ?? null;
}

export function useActiveFeed() {
  const query = useQuery(feedsQuery);
  return {
    query,
    feed: pickActiveFeed(query.data),
    feedId: pickActiveFeed(query.data)?.id ?? null,
  };
}

export function boardQuery(feedId: string) {
  return queryOptions({
    queryKey: feedKeys.board(feedId),
    queryFn: ({ signal }) => feedApi.board(feedId, signal),
    // SSE keeps this fresh; the interval is only a safety net while the stream is down.
    refetchInterval: 120_000,
  });
}

export function useBoard(feedId: string | null) {
  return useQuery({ ...boardQuery(feedId ?? ""), enabled: !!feedId });
}

/** Snoozed cards of the feed, soonest back first (the tray at the end of the feed). */
export function useSnoozedCards(feedId: string | null) {
  return useQuery({
    queryKey: feedKeys.snoozed(feedId ?? ""),
    queryFn: ({ signal }) => feedApi.snoozed(feedId ?? "", signal),
    enabled: !!feedId,
    // Feed events keep it fresh; the interval is only a safety net while the stream is down.
    refetchInterval: 300_000,
  });
}

export function cardQuery(cardId: string) {
  return queryOptions({
    queryKey: feedKeys.card(cardId),
    queryFn: ({ signal }) => feedApi.card(cardId, signal),
    staleTime: 30_000,
  });
}

export function useCard(cardId: string | null) {
  return useQuery({ ...cardQuery(cardId ?? ""), enabled: !!cardId });
}

export function prefetchCard(queryClient: QueryClient, cardId: string): void {
  void queryClient.prefetchQuery(cardQuery(cardId));
}

function cardThreadQuery(cardId: string, threadKey: string, images: boolean) {
  return queryOptions({
    queryKey: feedKeys.cardThread(threadKey, cardId, images),
    queryFn: ({ signal }) => feedApi.cardThread(cardId, images, signal),
    staleTime: 60_000,
  });
}

export function useCardThread(cardId: string | null, threadKey: string | null, images: boolean) {
  return useQuery({
    ...cardThreadQuery(cardId ?? "", threadKey ?? "", images),
    enabled: !!cardId,
    placeholderData: keepPreviousData,
  });
}

/** Warms a card's email (as the thread drawer first opens it), so `o` never waits (U3). */
export function prefetchCardThread(
  queryClient: QueryClient,
  cardId: string,
  threadKey: string | null,
): void {
  if (!threadKey) return;
  void queryClient.prefetchQuery(cardThreadQuery(cardId, threadKey, false));
}

// ---------------------------------------------------------------------------------------------
// Cache helpers used by optimistic card operations

/** Summary fields of a full card, for patching the board list. */
export function toSummary(card: CardView): CardSummary {
  const {
    blocks: _blocks,
    actions: _actions,
    sourceRunIds: _runs,
    history: _history,
    receipts: _receipts,
    updatePending: _pending,
    mailCheck: _mailCheck,
    drafting: _drafting,
    ...summary
  } = card;
  return summary;
}

export function findBoardCard(
  queryClient: QueryClient,
  cardId: string,
): { feedId: string; card: CardSummary } | null {
  for (const [key, board] of queryClient.getQueriesData<BoardView>({ queryKey: ["board"] })) {
    const hit = board?.cards.find((c) => c.id === cardId);
    if (hit) return { feedId: String(key[1]), card: hit };
  }
  return null;
}

/** Replace one card in every cached board (and add nothing if it is absent). */
export function patchBoardCard(
  queryClient: QueryClient,
  cardId: string,
  patch: (card: CardSummary) => CardSummary,
): void {
  queryClient.setQueriesData<BoardView>({ queryKey: ["board"] }, (board) => {
    if (!board || !board.cards.some((c) => c.id === cardId)) return board;
    return { ...board, cards: board.cards.map((c) => (c.id === cardId ? patch(c) : c)) };
  });
}

/** Store a server card (full view) in the card cache and the board list. */
export function acceptCard(queryClient: QueryClient, card: CardView): void {
  queryClient.setQueryData(feedKeys.card(card.id), card);
  const summary = toSummary(card);
  patchBoardCard(queryClient, card.id, () => summary);
}

export function invalidateBoards(queryClient: QueryClient, feedId?: string): void {
  void queryClient.invalidateQueries({ queryKey: feedId ? feedKeys.board(feedId) : ["board"] });
  void queryClient.invalidateQueries({
    queryKey: feedId ? feedKeys.snoozed(feedId) : ["snoozed"],
  });
}
